import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { getSiteConfig } from "@/config/siteConfigs";
import styles from "./FloatingCallButton.module.scss";

interface ContactMethod {
  type: "whatsapp" | "telegram" | "email" | "call";
  label: string;
  handler: () => void;
  delay: string;
}

const FloatingCallButton: React.FC = () => {
  const [isPhoneActive, setIsPhoneActive] = useState(true);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { t } = useTranslation("common");
  const siteConfig = getSiteConfig();

  // Анімація зміни іконок (зупиняється коли меню відкрите)
  useEffect(() => {
    if (isMenuOpen) return;

    const interval = setInterval(() => {
      setIsPhoneActive((prevState) => !prevState);
    }, 3000);

    return () => clearInterval(interval);
  }, [isMenuOpen]);

  const toggleMenu = () => {
    setIsMenuOpen((prev) => !prev);
  };

  // Отримуємо доступні методи зв'язку
  const getAvailableContactMethods = (): ContactMethod[] => {
    const methods: ContactMethod[] = [];

    // Telegram (якщо доступний)
    if (siteConfig.social.telegram !== "#") {
      methods.push({
        type: "telegram",
        label: "Telegram",
        handler: () => window.open(siteConfig.social.telegram, "_blank"),
        delay: isMenuOpen ? "0.05s" : `${0.25 - methods.length * 0.05}s`,
      });
    }

    // WhatsApp (якщо доступний)
    if (siteConfig.social.whatsapp !== "#") {
      methods.push({
        type: "whatsapp",
        label: "WhatsApp",
        handler: () => window.open(siteConfig.social.whatsapp, "_blank"),
        delay: isMenuOpen ? `${0.05 + methods.length * 0.05}s` : `${0.25 - methods.length * 0.05}s`,
      });
    }

    // Email
    methods.push({
      type: "email",
      label: "Email",
      handler: () => (window.location.href = `mailto:${siteConfig.contact.email}`),
      delay: isMenuOpen ? `${0.05 + methods.length * 0.05}s` : `${0.25 - methods.length * 0.05}s`,
    });

    // Call
    methods.push({
      type: "call",
      label: t("floatingButton.call", "Anrufen"),
      handler: () => (window.location.href = `tel:${siteConfig.contact.phone.replace(/\s/g, "")}`),
      delay: isMenuOpen ? `${0.05 + methods.length * 0.05}s` : "0.05s",
    });

    return methods;
  };

  const contactMethods = getAvailableContactMethods();

  // Рендеринг іконки в залежності від типу
  const renderIcon = (type: ContactMethod["type"]) => {
    switch (type) {
      case "whatsapp":
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"
              fill="currentColor"
            />
          </svg>
        );
      case "telegram":
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.446 1.394c-.14.18-.357.295-.6.295-.002 0-.003 0-.005 0l.213-3.054 5.56-5.022c.24-.213-.054-.334-.373-.121l-6.869 4.326-2.96-.924c-.64-.203-.658-.64.135-.954l11.566-4.458c.538-.196 1.006.128.832.941z"
              fill="currentColor"
            />
          </svg>
        );
      case "email":
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"
              fill="currentColor"
            />
          </svg>
        );
      case "call":
        return (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"
              fill="currentColor"
            />
          </svg>
        );
    }
  };

  return (
    <div className={styles.floatingCallButton}>
      {/* Варіанти зв'язку */}
      <div className={`${styles.contactOptions} ${isMenuOpen ? styles.open : ""}`}>
        {contactMethods.map((method) => (
          <button
            key={method.type}
            className={`${styles.contactOption} ${styles[method.type]}`}
            onClick={method.handler}
            style={{ transitionDelay: method.delay }}
          >
            <div className={styles.optionContent}>
              <div className={styles.iconCircle}>{renderIcon(method.type)}</div>
              <span className={styles.label}>{method.label}</span>
            </div>
          </button>
        ))}
      </div>

      {/* Головна кнопка */}
      <button
        className={`${styles.mainButton} ${isMenuOpen ? styles.open : ""}`}
        onClick={toggleMenu}
      >
        <div className={styles.buttonContent}>
          {!isMenuOpen ? (
            <>
              <img
                src="/images/phone.svg"
                alt="Phone"
                className={`${styles.icon} ${
                  isPhoneActive ? styles.phoneIconActive : styles.phoneIconInactive
                }`}
              />
              <img
                src="/images/letter.svg"
                alt="Letter"
                className={`${styles.icon} ${
                  !isPhoneActive ? styles.letterIconActive : styles.letterIconInactive
                }`}
              />
            </>
          ) : (
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="#fff"
              xmlns="http://www.w3.org/2000/svg"
              className={styles.closeIcon}
            >
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            </svg>
          )}
        </div>
      </button>
    </div>
  );
};

export default FloatingCallButton;
